/**
 * Mlyn Draggable Slider
 * Shared draggable/momentum slider functionality for featured collection sections
 * Optimized for performance - single initialization, event delegation
 */

(function () {
    'use strict';

    // Configuration
    const CONFIG = {
        SENSITIVITY: 1.5,    // Drag multiplier
        FRICTION: 0.95,      // Momentum friction (closer to 1 = more slide)
        MIN_VELOCITY: 0.1,   // Stop threshold
        DRAG_THRESHOLD: 5,   // Pixels before considered dragging
        DIRECTION_THRESHOLD: 10 // Pixels to determine scroll direction
    };

    // Slider class cache
    const SLIDER_CLASSES = [
        '.draggable-slider',
        '.draggable-slider-fixed',
        '.mlyns-scroll-container',
        '.mlyn--scroll-container'
    ];

    /**
     * Initialize draggable functionality on a slider element
     */
    function initSlider(slider) {
        if (slider.dataset.draggableInit) return; // Already initialized
        slider.dataset.draggableInit = 'true';

        let isDown = false;
        let startX = 0;
        let startY = 0; // Track Y position for direction detection
        let scrollLeft = 0;
        let velX = 0;
        let momentumID = null;
        let isDragging = false;
        let directionLocked = null; // 'horizontal', 'vertical', or null

        // Mouse events
        slider.addEventListener('mousedown', onPointerDown, { passive: true });
        slider.addEventListener('mousemove', onPointerMove, { passive: false });
        slider.addEventListener('mouseup', onPointerUp, { passive: true });
        slider.addEventListener('mouseleave', onPointerLeave, { passive: true });

        // Touch events
        slider.addEventListener('touchstart', onTouchStart, { passive: true });
        slider.addEventListener('touchmove', onTouchMove, { passive: false });
        slider.addEventListener('touchend', onPointerUp, { passive: true });

        // Prevent link clicks during drag
        slider.querySelectorAll('a, .prevent-drag-click').forEach(link => {
            link.addEventListener('click', (e) => {
                if (isDragging) {
                    e.preventDefault();
                    e.stopPropagation();
                }
            });
            link.ondragstart = () => false;
        });

        function onPointerDown(e) {
            isDown = true;
            isDragging = false;
            slider.classList.add('active');
            cancelAnimationFrame(momentumID);
            startX = e.pageX - slider.offsetLeft;
            scrollLeft = slider.scrollLeft;
        }

        function onTouchStart(e) {
            isDown = true;
            isDragging = false;
            directionLocked = null; // Reset direction lock
            slider.classList.add('active');
            cancelAnimationFrame(momentumID);
            startX = e.touches[0].pageX - slider.offsetLeft;
            startY = e.touches[0].pageY; // Track Y position
            scrollLeft = slider.scrollLeft;
        }

        function onPointerMove(e) {
            if (!isDown) return;
            e.preventDefault();
            const x = e.pageX - slider.offsetLeft;
            handleMove(x);
        }

        function onTouchMove(e) {
            if (!isDown) return;

            const currentX = e.touches[0].pageX;
            const currentY = e.touches[0].pageY;
            const diffX = Math.abs(currentX - (startX + slider.offsetLeft));
            const diffY = Math.abs(currentY - startY);

            // Determine direction if not yet locked
            if (directionLocked === null && (diffX > CONFIG.DIRECTION_THRESHOLD || diffY > CONFIG.DIRECTION_THRESHOLD)) {
                if (diffY > diffX) {
                    // User is scrolling vertically - let the page scroll naturally
                    directionLocked = 'vertical';
                    isDown = false;
                    slider.classList.remove('active');
                    return;
                } else {
                    // User is scrolling horizontally - intercept for carousel
                    directionLocked = 'horizontal';
                }
            }

            // Only handle horizontal scrolling
            if (directionLocked === 'vertical') return;

            // Prevent vertical scroll when dragging horizontally
            if (directionLocked === 'horizontal') {
                e.preventDefault();
            }

            const x = currentX - slider.offsetLeft;
            handleMove(x);
        }

        function handleMove(x) {
            if (Math.abs(x - startX) > CONFIG.DRAG_THRESHOLD) isDragging = true;
            const walk = (x - startX) * CONFIG.SENSITIVITY;
            const prevScroll = slider.scrollLeft;
            slider.scrollLeft = scrollLeft - walk;
            velX = slider.scrollLeft - prevScroll;
        }

        function onPointerUp() {
            if (!isDown) return;
            isDown = false;
            directionLocked = null; // Reset direction lock
            slider.classList.remove('active');
            beginMomentum();
            setTimeout(() => { isDragging = false; }, 50);
        }

        function onPointerLeave() {
            if (isDown) {
                isDown = false;
                slider.classList.remove('active');
                beginMomentum();
            }
        }

        function beginMomentum() {
            cancelAnimationFrame(momentumID);

            function step() {
                if (Math.abs(velX) < CONFIG.MIN_VELOCITY) return;
                slider.scrollLeft += velX;
                velX *= CONFIG.FRICTION;
                momentumID = requestAnimationFrame(step);
            }

            momentumID = requestAnimationFrame(step);
        }
    }

    /**
     * Initialize all sliders on the page
     */
    function initAllSliders() {
        const selector = SLIDER_CLASSES.join(', ');
        document.querySelectorAll(selector).forEach(initSlider);
    }

    // Initialize on DOM ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initAllSliders);
    } else {
        initAllSliders();
    }

    // Re-initialize when new content is added (for AJAX/Alpine)
    const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            mutation.addedNodes.forEach((node) => {
                if (node.nodeType === 1) {
                    const selector = SLIDER_CLASSES.join(', ');
                    if (node.matches && node.matches(selector)) {
                        initSlider(node);
                    }
                    node.querySelectorAll && node.querySelectorAll(selector).forEach(initSlider);
                }
            });
        });
    });

    observer.observe(document.body, { childList: true, subtree: true });

    // Expose for manual init if needed
    window.MlynDraggableSlider = { init: initSlider, initAll: initAllSliders };
})();
